<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import EvolutionAddGroupParticipantsDialog from 'dashboard/components/widgets/conversation/EvolutionAddGroupParticipantsDialog.vue';

const props = defineProps({
  contact: { type: Object, required: true },
  conversationId: { type: [Number, String], required: true },
});

const emit = defineEmits(['membersChanged']);

const PAGE_SIZE = 20;
const ADMIN_ROLES = ['admin', 'superadmin'];
const MAXIMUM_FILE_UPLOAD_SIZE = 4; // in MB

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const addDialogRef = ref(null);
const removeConfirmRef = ref(null);

// Name/description/picture start from the group Contact's own cached fields (no eager Evolution
// call just to open this dialog) and are only sent to Evolution when actually changed and saved.
const name = ref(props.contact.name || '');
const description = ref(props.contact.additional_attributes?.description || '');
const pictureUrl = ref(props.contact.thumbnail || '');
const isSavingProfile = ref(false);
const isUploadingPicture = ref(false);

const isLoadingMembers = ref(false);
const participants = ref([]);
const removingId = ref(null);
const changingRoleId = ref(null);
const page = ref(1);
const participantToRemove = ref(null);
const openMenuParticipantId = ref(null);
const activeTabIndex = ref(0);

const tabs = computed(() => [
  { key: 'profile', label: t('CONTACT_PANEL.EVOLUTION_GROUP.TAB_PROFILE') },
  {
    key: 'members',
    label: t('CONTACT_PANEL.EVOLUTION_GROUP.TAB_MEMBERS'),
    count: participants.value.length,
  },
]);

const handleTabChanged = tab => {
  activeTabIndex.value = tabs.value.findIndex(item => item.key === tab.key);
};

const totalPages = computed(() =>
  Math.max(1, Math.ceil(participants.value.length / PAGE_SIZE))
);

const paginatedParticipants = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE;
  return participants.value.slice(start, start + PAGE_SIZE);
});

const isAdmin = participant => ADMIN_ROLES.includes(participant.admin);

// Evolution addresses participants by full JID (e.g. "5511999998888@s.whatsapp.net") - strip the
// suffix so the list shows a clean number instead of a raw technical identifier.
const formatParticipantPhone = participant =>
  participant.phoneNumber?.split('@')[0] || '';

const participantLabel = participant =>
  participant.name || formatParticipantPhone(participant) || participant.id;

const resetProfileFields = () => {
  name.value = props.contact.name || '';
  description.value = props.contact.additional_attributes?.description || '';
  pictureUrl.value = props.contact.thumbnail || '';
};

// Uploads to Chatwoot's own storage first (generic blob upload, not tied to any record) to get a
// publicly-fetchable URL, since Evolution's updateGroupPicture only accepts a URL it can fetch
// itself - the actual WhatsApp-side change only happens once "Save" is pressed below.
const handlePictureUpload = async ({ file }) => {
  if (!checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
    useAlert(
      t('CONTACT_PANEL.EVOLUTION_GROUP.PICTURE_UPLOAD_SIZE_ERROR', {
        size: MAXIMUM_FILE_UPLOAD_SIZE,
      })
    );
    return;
  }

  isUploadingPicture.value = true;
  try {
    const { fileUrl } = await uploadFile(file);
    if (fileUrl) pictureUrl.value = fileUrl;
  } catch (error) {
    useAlert(t('CONTACT_PANEL.EVOLUTION_GROUP.PICTURE_UPLOAD_ERROR'));
  } finally {
    isUploadingPicture.value = false;
  }
};

// Evolution's group/participants endpoint returns the full member list in one call (no
// server-side pagination) - fetched only when the dialog is actually opened, then paginated
// here on the client so large groups stay snappy.
const loadParticipants = async () => {
  isLoadingMembers.value = true;
  try {
    const { data } = await ConversationAPI.getEvolutionGroupInfo(
      props.conversationId
    );
    participants.value = data.participants || [];
    emit('membersChanged', participants.value.length);
  } catch (error) {
    useAlert(t('CONTACT_PANEL.EVOLUTION_GROUP.LOAD_ERROR'));
  } finally {
    isLoadingMembers.value = false;
  }
};

const open = () => {
  resetProfileFields();
  page.value = 1;
  activeTabIndex.value = 0;
  dialogRef.value?.open();
  loadParticipants();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const saveProfile = async () => {
  isSavingProfile.value = true;
  try {
    await ConversationAPI.updateEvolutionGroup(props.conversationId, {
      subject: name.value !== props.contact.name ? name.value : undefined,
      description:
        description.value !==
        (props.contact.additional_attributes?.description || '')
          ? description.value
          : undefined,
      pictureUrl:
        pictureUrl.value !== (props.contact.thumbnail || '')
          ? pictureUrl.value
          : undefined,
    });
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      name: name.value,
      additional_attributes: {
        ...(props.contact.additional_attributes || {}),
        description: description.value,
      },
    });
    await store.dispatch('contacts/fetchContactableInbox', props.contact.id);
    useAlert(t('CONTACT_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACT_FORM.ERROR_MESSAGE'));
  } finally {
    isSavingProfile.value = false;
  }
};

const nextPage = () => {
  page.value = Math.min(page.value + 1, totalPages.value);
};

const previousPage = () => {
  page.value = Math.max(page.value - 1, 1);
};

const confirmRemoveParticipant = participant => {
  participantToRemove.value = participant;
  removeConfirmRef.value?.open();
};

const removeParticipant = async () => {
  const participant = participantToRemove.value;
  if (!participant || removingId.value) return;
  removingId.value = participant.id;
  try {
    await ConversationAPI.removeEvolutionGroupParticipants(
      props.conversationId,
      [participant.phoneNumber || participant.id]
    );
    participants.value = participants.value.filter(
      p => p.id !== participant.id
    );
    if (paginatedParticipants.value.length === 0 && page.value > 1) {
      page.value -= 1;
    }
    emit('membersChanged', participants.value.length);
    useAlert(t('CONTACT_PANEL.EVOLUTION_GROUP.MEMBER_REMOVED'));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.EVOLUTION_GROUP.MEMBER_REMOVE_ERROR'));
  } finally {
    removingId.value = null;
    participantToRemove.value = null;
  }
};

const toggleParticipantRole = async participant => {
  if (changingRoleId.value) return;
  changingRoleId.value = participant.id;
  const promoting = !isAdmin(participant);
  try {
    if (promoting) {
      await ConversationAPI.promoteEvolutionGroupParticipant(
        props.conversationId,
        participant.phoneNumber || participant.id
      );
      participant.admin = 'admin';
      useAlert(t('CONTACT_PANEL.EVOLUTION_GROUP.MEMBER_PROMOTED'));
    } else {
      await ConversationAPI.demoteEvolutionGroupParticipant(
        props.conversationId,
        participant.phoneNumber || participant.id
      );
      participant.admin = null;
      useAlert(t('CONTACT_PANEL.EVOLUTION_GROUP.MEMBER_DEMOTED'));
    }
  } catch (error) {
    useAlert(t('CONTACT_PANEL.EVOLUTION_GROUP.MEMBER_ROLE_ERROR'));
  } finally {
    changingRoleId.value = null;
  }
};

// A tap-to-open menu (rather than hover-reveal icons) so member actions are actually reachable
// on touch devices, not just with a mouse.
const toggleParticipantMenu = participant => {
  openMenuParticipantId.value =
    openMenuParticipantId.value === participant.id ? null : participant.id;
};

const closeParticipantMenu = () => {
  openMenuParticipantId.value = null;
};

const participantMenuItems = participant => [
  {
    icon: 'i-lucide-shield',
    label: isAdmin(participant)
      ? t('CONTACT_PANEL.EVOLUTION_GROUP.DEMOTE')
      : t('CONTACT_PANEL.EVOLUTION_GROUP.PROMOTE'),
    action: 'toggle-role',
    value: participant.id,
  },
  {
    icon: 'i-lucide-user-minus',
    label: t('CONTACT_PANEL.EVOLUTION_GROUP.REMOVE_MEMBER'),
    action: 'delete',
    value: participant.id,
  },
];

const handleParticipantMenuAction = ({ action }, participant) => {
  closeParticipantMenu();
  if (action === 'toggle-role') {
    toggleParticipantRole(participant);
  } else if (action === 'delete') {
    confirmRemoveParticipant(participant);
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog ref="dialogRef" width="lg" overflow-y-auto>
    <div class="flex flex-col gap-5">
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <span class="i-lucide-users-round size-4 text-n-slate-11" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACT_PANEL.EVOLUTION_GROUP.MANAGE_GROUP') }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            @tab-changed="handleTabChanged"
          />
          <Button icon="i-lucide-x" slate ghost sm @click="closeDialog" />
        </div>
      </div>

      <!-- Profile: name, picture, description -->
      <div v-show="activeTabIndex === 0" class="flex flex-col gap-4">
        <div class="flex items-center gap-4">
          <div class="relative shrink-0">
            <Avatar
              :src="pictureUrl"
              :name="name"
              :size="72"
              allow-upload
              @upload="handlePictureUpload"
            >
              <template
                #overlay="{ handleUpload, fileInputRef, handleImageUpload }"
              >
                <input
                  :ref="el => (fileInputRef.value = el)"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
                  class="hidden"
                  @change="handleImageUpload"
                />
                <button
                  type="button"
                  class="absolute z-10 flex items-center justify-center rounded-full -bottom-1 -right-1 size-7 bg-n-solid-3 outline outline-1 outline-n-weak text-n-slate-11 hover:bg-n-solid-2"
                  :title="t('CONTACT_PANEL.EVOLUTION_GROUP.UPLOAD_PICTURE')"
                  @click="handleUpload"
                >
                  <Spinner
                    v-if="isUploadingPicture"
                    class="size-3.5 text-n-slate-11"
                  />
                  <span v-else class="i-lucide-camera size-3.5" />
                </button>
              </template>
            </Avatar>
          </div>
          <div class="flex flex-col flex-1 min-w-0">
            <NextInput
              v-model="name"
              :label="t('CONTACT_PANEL.EVOLUTION_GROUP.NAME')"
            />
          </div>
        </div>
        <TextArea
          v-model="description"
          :label="t('CONTACT_PANEL.EVOLUTION_GROUP.DESCRIPTION')"
          custom-text-area-class="h-24 overflow-y-auto"
        />
      </div>

      <!-- Members -->
      <div v-show="activeTabIndex === 1" class="flex flex-col gap-3">
        <div class="flex items-center justify-end">
          <Button
            icon="i-lucide-user-plus"
            :label="
              t('CONTACT_PANEL.ADD_EVOLUTION_GROUP_PARTICIPANTS.MENU_LABEL')
            "
            slate
            faded
            xs
            @click="addDialogRef?.open()"
          />
        </div>

        <Spinner
          v-if="isLoadingMembers"
          class="self-center size-5 text-n-slate-11"
        />
        <template v-else>
          <div class="flex flex-col min-h-[16rem] divide-y divide-n-weak">
            <div
              v-for="participant in paginatedParticipants"
              :key="participant.id"
              class="flex items-center gap-3 py-2"
            >
              <Avatar
                :src="participant.imgUrl"
                :name="participantLabel(participant)"
                :size="36"
              />
              <div class="flex flex-col flex-1 min-w-0">
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ participantLabel(participant) }}
                </span>
                <span
                  v-if="participant.name && participant.phoneNumber"
                  class="text-xs truncate text-n-slate-11"
                >
                  {{ formatParticipantPhone(participant) }}
                </span>
              </div>
              <Label
                v-if="isAdmin(participant)"
                :label="t('CONTACT_PANEL.EVOLUTION_GROUP.ADMIN_BADGE')"
                color="amber"
                compact
              />
              <Spinner
                v-if="
                  changingRoleId === participant.id ||
                  removingId === participant.id
                "
                class="size-4 text-n-slate-11"
              />
              <div
                v-else
                v-on-clickaway="() => closeParticipantMenu()"
                class="relative"
              >
                <Button
                  icon="i-lucide-more-vertical"
                  slate
                  ghost
                  sm
                  @click="toggleParticipantMenu(participant)"
                />
                <DropdownMenu
                  v-if="openMenuParticipantId === participant.id"
                  :menu-items="participantMenuItems(participant)"
                  class="mt-1 ltr:right-0 rtl:left-0 top-full"
                  @action="handleParticipantMenuAction($event, participant)"
                />
              </div>
            </div>
            <p
              v-if="!paginatedParticipants.length"
              class="text-sm text-n-slate-11"
            >
              {{ t('CONTACT_PANEL.EVOLUTION_GROUP.NO_MEMBERS') }}
            </p>
          </div>

          <div
            v-if="totalPages > 1"
            class="flex items-center justify-between pt-2 border-t border-n-weak"
          >
            <Button
              :label="t('CONTACT_PANEL.EVOLUTION_GROUP.PREVIOUS_PAGE')"
              variant="faded"
              color="slate"
              xs
              :disabled="page === 1"
              @click="previousPage"
            />
            <span class="text-xs text-n-slate-11">
              {{
                t('CONTACT_PANEL.EVOLUTION_GROUP.PAGE_OF', {
                  page,
                  total: totalPages,
                })
              }}
            </span>
            <Button
              :label="t('CONTACT_PANEL.EVOLUTION_GROUP.NEXT_PAGE')"
              variant="faded"
              color="slate"
              xs
              :disabled="page === totalPages"
              @click="nextPage"
            />
          </div>
        </template>
      </div>
    </div>

    <template v-if="activeTabIndex === 0" #footer>
      <Button
        class="w-full"
        :label="t('CONTACT_PANEL.EVOLUTION_GROUP.SAVE')"
        :is-loading="isSavingProfile"
        @click="saveProfile"
      />
    </template>
  </Dialog>

  <Dialog
    ref="removeConfirmRef"
    type="alert"
    width="sm"
    :title="t('CONTACT_PANEL.EVOLUTION_GROUP.REMOVE_CONFIRM_TITLE')"
    :description="
      t('CONTACT_PANEL.EVOLUTION_GROUP.REMOVE_CONFIRM_DESCRIPTION', {
        name: participantToRemove ? participantLabel(participantToRemove) : '',
      })
    "
    :confirm-button-label="t('CONTACT_PANEL.EVOLUTION_GROUP.REMOVE_MEMBER')"
    :is-loading="!!removingId"
    @confirm="removeParticipant"
  />

  <EvolutionAddGroupParticipantsDialog
    ref="addDialogRef"
    :conversation-id="conversationId"
    @added="loadParticipants"
  />
</template>
